import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, ThumbsUp, Share2, Users, Calendar, Clock, User, Gift, Heart, MessageSquare, Zap, Award, Loader } from 'lucide-react';
import { demoStreams, demoNfts } from '@/services/demoData';
import { toast } from 'react-hot-toast';
import { sendTip, demoMode } from '@/services/paymentSystem';
import LivepeerPlayer from '@/components/video/LivepeerPlayer';

// Import the 3D background directly (it handles its own loading)
import StreamBackground from '@/components/3d/StreamBackground';

export default function StreamPage() {
  const { id } = useParams<{ id: string }>();
  const [message, setMessage] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(124);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('5');
  const [showEmojis, setShowEmojis] = useState(false);
  const [creatorAddress, setCreatorAddress] = useState('0x742d35Cc6634C0532925a3b844Bc454e4438f44e'); // Sample EVM address
  const [isSendingTip, setIsSendingTip] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  const [chatMessages, setChatMessages] = useState([
    { id: 1, user: 'User1', message: 'Great stream!', timestamp: new Date(Date.now() - 60000), isHighlighted: false },
    { id: 2, user: 'User2', message: 'How did you learn this technique?', timestamp: new Date(Date.now() - 120000), isHighlighted: false },
    { id: 3, user: 'User3', message: 'This is amazing content', timestamp: new Date(Date.now() - 180000), isHighlighted: false },
    { id: 4, user: 'User4', message: 'Can you explain that last part again?', timestamp: new Date(Date.now() - 240000), isHighlighted: false },
    { id: 5, user: 'VIP_User', message: '👍 👍 👍', timestamp: new Date(Date.now() - 300000), isHighlighted: true },
  ]);
  
  // Use demo data
  const stream = demoStreams[0]; // Using first stream as example
  
  // Simulate new chat messages coming in
  useEffect(() => {
    const messages = [
      'Amazing content!',
      'Can you show that again?',
      'First time watching, loving it!',
      'Subscribed yesterday, best decision ever',
      'How long have you been doing this?',
      'Greetings from Germany!',
      'This is revolutionary',
      '🔥🔥🔥',
      'Just shared this with my friends',
      'When is your next stream?'
    ];
    
    const users = ['NewUser1', 'CryptoFan', 'Web3Lover', 'NFTCollector', 'BlockchainDev'];
    
    const interval = setInterval(() => {
      const randomMessage = messages[Math.floor(Math.random() * messages.length)];
      const randomUser = users[Math.floor(Math.random() * users.length)];
      
      setChatMessages(prev => [
        { 
          id: Date.now(), 
          user: randomUser, 
          message: randomMessage, 
          timestamp: new Date(),
          isHighlighted: Math.random() > 0.8 // 20% chance of being highlighted
        },
        ...prev
      ]);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 0;
    }
  }, [chatMessages]);
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      setChatMessages([
        { id: Date.now(), user: 'You', message, timestamp: new Date(), isHighlighted: false },
        ...chatMessages
      ]);
      setMessage('');
      setShowEmojis(false);
    }
  };
  
  const handleLike = () => {
    if (!isLiked) {
      setLikeCount(prev => prev + 1);
      
      // Animate heart
      const heart = document.createElement('div');
      heart.className = 'absolute text-red-500 text-2xl';
      heart.style.left = `${Math.random() * 80 + 10}%`;
      heart.style.top = '100%';
      heart.innerHTML = '❤️';
      
      if (videoRef.current?.parentElement) {
        videoRef.current.parentElement.appendChild(heart);
        
        // Animate the heart floating up
        heart.animate(
          [
            { transform: 'translateY(0) scale(1)', opacity: 1 },
            { transform: 'translateY(-100px) scale(1.5)', opacity: 0.5 },
            { transform: 'translateY(-200px) scale(0.5)', opacity: 0 }
          ],
          {
            duration: 1500,
            easing: 'ease-out'
          }
        );
        
        // Remove the heart element after animation
        setTimeout(() => {
          heart.remove();
        }, 1500);
      }
    } else {
      setLikeCount(prev => prev - 1);
    }
    
    setIsLiked(!isLiked);
  };
  
  const handleTip = () => {
    setShowTipModal(true);
  };
  
  const handleSendTip = async () => {
    try {
      setIsSendingTip(true);
      
      // Use our payment system to send the tip
      const result = await demoMode.sendTip(creatorAddress, tipAmount);
      
      if (result.status === 'success') {
        const txHash = result.txHash;
        
        toast.success(
          <div>
            <p>Sent {tipAmount} DUSD tip to {stream.creatorName}!</p>
            <p className="text-xs mt-1 text-gray-400">
              Transaction: {txHash.substring(0, 10)}...{txHash.substring(58)}
            </p>
          </div>
        );
        
        // Add to chat as a system message
        setChatMessages([
          { 
            id: Date.now(), 
            user: 'System', 
            message: `${stream.creatorName} received a ${tipAmount} DUSD tip!`, 
            timestamp: new Date(),
            isHighlighted: true 
          },
          ...chatMessages
        ]);
        
        setShowTipModal(false);
      } else {
        toast.error('Failed to send tip. Please try again.');
      }
    } catch (error) {
      console.error('Error sending tip:', error);
      toast.error('Failed to send tip. Please try again.');
    } finally {
      setIsSendingTip(false);
    }
  };
  
  return (
    <div className="container-custom py-8 relative">
      {/* 3D Background */}
      <StreamBackground />
      
      {/* Tip Modal */}
      {showTipModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-dark-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-3d"
          >
            <h2 className="text-2xl font-bold mb-4 text-shadow">Tip Creator</h2>
            <p className="text-gray-400 mb-6">Send a tip to support {stream.creatorName} and their content</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-shadow">Amount (DUSD)</label>
                <div className="flex space-x-2 mb-4">
                  {['5', '10', '25', '50'].map(amount => (
                    <Button
                      key={amount}
                      variant={tipAmount === amount ? 'default' : 'outline'}
                      onClick={() => setTipAmount(amount)}
                      className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                    >
                      {amount}
                    </Button>
                  ))}
                </div>
                <input
                  type="number"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-md px-3 py-2 shadow-3d focus:shadow-3d-hover transition-all duration-300"
                  placeholder="Custom amount"
                />
              </div>
              
              <div className="bg-dark-700 rounded-lg p-4 shadow-3d">
                <div className="flex justify-between mb-2">
                  <span className="text-shadow">Tip Amount</span>
                  <span className="text-shadow">{tipAmount} DUSD</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Platform Fee (2%)</span>
                  <span>{(parseFloat(tipAmount) * 0.02).toFixed(2)} DUSD</span>
                </div>
                <div className="mt-2 pt-2 border-t border-dark-600">
                  <p className="text-sm text-gray-400 mb-1">Recipient Address:</p>
                  <div className="bg-dark-800 p-2 rounded text-xs font-mono break-all shadow-3d">
                    {creatorAddress}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button 
                variant="outline" 
                className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                onClick={() => setShowTipModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                onClick={handleSendTip} 
                disabled={isSendingTip}
              >
                {isSendingTip ? (
                  <>
                    <span className="animate-spin mr-2">◌</span>
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Send Tip
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 z-10 relative">
        {/* Main Content - Video Player and Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Video Player */}
          <motion.div
            className="relative aspect-video rounded-2xl overflow-hidden shadow-3d"
            whileHover={{ scale: 1.01, rotateX: 2, rotateY: 2, transition: { duration: 0.3 } }}
          >
            {stream?.playbackId ? (
              <LivepeerPlayer playbackId={stream.playbackId} ref={videoRef} />
            ) : (
              <div className="absolute top-4 left-4 bg-red-600 text-white px-2 py-1 rounded-md text-sm font-medium flex items-center shadow-3d">
                <span className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></span>
                LIVE
              </div>
            )}
            
            <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-md text-sm shadow-3d">
              <Users className="inline-block w-4 h-4 mr-1" />
              {stream.viewCount} viewers
            </div>
            
            {/* Floating reactions */}
            <div className="absolute bottom-4 left-4 flex space-x-2">
              {['❤️', '👍', '🔥', '😮', '🎉'].map((emoji, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-lg shadow-3d hover:shadow-3d-hover transition-all duration-300"
                  onClick={() => {
                    // Add emoji to chat
                    setChatMessages([
                      { id: Date.now(), user: 'You', message: emoji, timestamp: new Date(), isHighlighted: false },
                      ...chatMessages
                    ]);
                    
                    // Animate emoji
                    const emojiEl = document.createElement('div');
                    emojiEl.className = 'absolute text-2xl';
                    emojiEl.style.left = `${Math.random() * 80 + 10}%`;
                    emojiEl.style.bottom = '10%';
                    emojiEl.innerHTML = emoji;
                    
                    if (videoRef.current?.parentElement) {
                      videoRef.current.parentElement.appendChild(emojiEl);
                      
                      emojiEl.animate(
                        [
                          { transform: 'translateY(0) scale(1)', opacity: 1 },
                          { transform: 'translateY(-100px) scale(1.5)', opacity: 0.5 },
                          { transform: 'translateY(-200px) scale(0.5)', opacity: 0 }
                        ],
                        {
                          duration: 1500,
                          easing: 'ease-out'
                        }
                      );
                      
                      setTimeout(() => {
                        emojiEl.remove();
                      }, 1500);
                    }
                  }}
                >
                  {emoji}
                </motion.button>
              ))}
            </div>
          </motion.div>
          
          {/* Stream Info */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <h1 className="text-2xl font-bold mb-2 text-shadow">{stream.title}</h1>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 p-0.5 shadow-3d">
                  <div className="w-full h-full rounded-full bg-dark-800 flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1633332755192-727a05c4013d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1180&q=80"
                      alt={stream.creatorName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-shadow">{stream.creatorName}</p>
                  <p className="text-sm text-gray-400">1.2K followers</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button className="shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300">
                  <User className="w-4 h-4 mr-2" />
                  Follow
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleTip}
                  className="shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Tip Creator
                </Button>
              </div>
            </div>
            <p className="text-gray-300">{stream.description}</p>
          </motion.div>
          
          {/* Action Buttons */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap gap-2 mb-8"
          >
            <Button 
              variant={isLiked ? "default" : "outline"} 
              className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
              onClick={handleLike}
            >
              <Heart className={`w-4 h-4 mr-2 ${isLiked ? "fill-current" : ""}`} />
              {isLiked ? 'Liked' : 'Like'} ({likeCount})
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
              onClick={handleTip}
            >
              <Zap className="w-4 h-4 mr-2" />
              Tip
            </Button>
          </motion.div>
          
          {/* Stream Details Tabs */}
          <Tabs defaultValue="about">
            <TabsList className="shadow-3d">
              <TabsTrigger 
                value="about"
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                About
              </TabsTrigger>
              <TabsTrigger 
                value="nfts"
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                NFTs
              </TabsTrigger>
              <TabsTrigger 
                value="schedule"
                className="hover:shadow-3d-hover transition-all duration-300"
              >
                Schedule
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="about" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium mb-2 text-shadow">Stream Details</h3>
                      <p className="text-gray-400">
                        {stream.description || "Join this interactive stream where we'll explore the latest developments in Web3 technology and discuss how they're changing the digital landscape."}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      <div className="flex items-start">
                        <Calendar className="w-5 h-5 text-primary-500 mt-0.5 mr-2" />
                        <div>
                          <p className="font-medium text-shadow">Started</p>
                          <p className="text-sm text-gray-400">
                            {new Date(stream.startTime).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Clock className="w-5 h-5 text-primary-500 mt-0.5 mr-2" />
                        <div>
                          <p className="font-medium text-shadow">Duration</p>
                          <p className="text-sm text-gray-400">
                            {stream.endTime ? 
                              `${Math.round((new Date(stream.endTime).getTime() - new Date(stream.startTime).getTime()) / 3600000)} hours` : 
                              'Ongoing'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <User className="w-5 h-5 text-primary-500 mt-0.5 mr-2" />
                        <div>
                          <p className="font-medium text-shadow">Creator</p>
                          <p className="text-sm text-gray-400">{stream.creatorName}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="nfts" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-medium mb-4 text-shadow">Stream NFTs</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                      >
                        <div className="bg-dark-700 rounded-lg overflow-hidden shadow-3d hover:shadow-3d-hover transition-all duration-300">
                          <div className="aspect-square">
                            <img 
                              src={`https://picsum.photos/seed/nft${i}/300/300`}
                              alt={`NFT ${i}`}
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            />
                          </div>
                          <div className="p-3">
                            <h4 className="font-medium text-shadow">Stream Moment #{i}</h4>
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-sm text-gray-400">Limited Edition</span>
                              <span className="text-primary-500 font-medium text-shadow">0.{i} DUSD</span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="schedule" className="mt-6">
              <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-medium mb-4 text-shadow">Upcoming Streams</h3>
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                        className="flex items-start pb-4 border-b last:border-0"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden mr-4 shadow-3d">
                          <img 
                            src={`https://picsum.photos/seed/stream${i+10}/300/300`}
                            alt={`Stream ${i}`}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          />
                        </div>
                        <div>
                          <h4 className="font-medium text-shadow">Upcoming Stream #{i}</h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {new Date(Date.now() + i * 86400000).toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="ml-auto shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                        >
                          Remind Me
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Right Column - Chat */}
        <div className="lg:col-span-1">
          <motion.div 
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="sticky top-24"
          >
            <Card className="h-[600px] flex flex-col bg-dark-800/80 backdrop-blur-sm border-dark-700 shadow-3d hover:shadow-3d-hover transition-all duration-300">
              <div className="p-4 border-b border-dark-700 flex justify-between items-center">
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-primary-500" />
                  <h3 className="font-medium text-shadow">Live Chat</h3>
                </div>
                <div className="flex items-center text-sm text-gray-400">
                  <Users className="w-4 h-4 mr-1" />
                  <span>{Math.floor(stream.viewCount * 0.8)} chatting</span>
                </div>
              </div>
              
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.map((msg) => (
                  <motion.div 
                    key={msg.id} 
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className={`flex items-start ${msg.isHighlighted ? 'bg-primary-500/10 p-2 rounded-lg shadow-3d' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mr-2 flex-shrink-0 flex items-center justify-center shadow-3d">
                      {msg.user.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <p className={`font-medium text-sm ${msg.user === 'VIP_User' || msg.isHighlighted ? 'text-primary-500 text-shadow' : ''}`}>
                          {msg.user}
                        </p>
                        {(msg.user === 'VIP_User' || msg.isHighlighted) && (
                          <span className="ml-1 bg-primary-500/20 text-primary-500 text-xs px-1 rounded shadow-3d">
                            VIP
                          </span>
                        )}
                        <span className="text-xs text-gray-500 ml-2">
                          {msg.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                      <p className="text-sm break-words">{msg.message}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="p-4 border-t border-dark-700">
                <form onSubmit={handleSendMessage} className="flex flex-col space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="w-full bg-dark-700 border border-dark-600 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 pr-10 shadow-3d focus:shadow-3d-hover transition-all duration-300"
                    />
                    <button 
                      type="button" 
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                      onClick={() => setShowEmojis(!showEmojis)}
                    >
                      😊
                    </button>
                  </div>
                  
                  {showEmojis && (
                    <div className="bg-dark-700 p-2 rounded-md grid grid-cols-8 gap-1 shadow-3d">
                      {['😊', '😂', '❤️', '👍', '🔥', '🎉', '👏', '😮', '🤔', '😎', '🙏', '💯', '🚀', '💪', '✨', '🌟'].map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          className="hover:bg-dark-600 p-1 rounded transition-transform duration-300 hover:scale-110"
                          onClick={() => {
                            setMessage(prev => prev + emoji);
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </div>
            </Card>
            
            {/* Stream Stats */}
            <Card className="mt-4 bg-dark-800/80 backdrop-blur-sm border-dark-700 shadow-3d hover:shadow-3d-hover transition-all duration-300">
              <CardContent className="p-4">
                <h3 className="font-medium mb-3 flex items-center text-shadow">
                  <Award className="w-4 h-4 mr-2 text-primary-500" />
                  Top Supporters
                </h3>
                <div className="space-y-3">
                  {['CryptoWhale', 'NFTCollector', 'Web3Fan'].map((supporter, index) => (
                    <motion.div
                      key={supporter}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mr-2 flex items-center justify-center text-xs shadow-3d">
                          {index + 1}
                        </div>
                        <span className="text-shadow">{supporter}</span>
                      </div>
                      <span className="text-primary-500 font-medium text-shadow">{(index + 1) * 50} DUSD</span>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
